import { Venue, VenuePinType } from "common/types";
import { formatDateShort, findImageByName } from "common/utils/general";
import OpeningHours from "@/components/VenueCard/TodaysOpeningHours";
import { bucketUrlPrefix } from "common/config";
import { useAuth, useFilters, useI18N, useZoomDetection } from "common/hooks";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import {
  MapPinIcon,
  BellIcon,
  PlusCircleIcon,
  HeartStraightIcon,
  BellRingingIcon,
  XIcon as XPhosphorIcon,
} from "@phosphor-icons/react";
import { BusinessTypeIcon } from "@/components/VenueCard/BusinessTypeIcon";
import { AccessibilityInfoIcon } from "@/components/VenueCard/SociabilityIcons";
import { XIcon } from "lucide-react";
import Modal from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import { useState, useRef, useEffect, useMemo, memo } from "react";
import { useAtom, useSetAtom } from "jotai";
import { hoveredVenueIdAtom } from "@/state/atoms";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader } from "@/shadcn/components/ui/card";
import { StandardButton } from "@/components/common/StandardButton";
import { useDarkMode } from "@/hooks/useDarkMode";
import { trackEvent } from "common/analytics";
import {
  pinTypeForVenue,
  getVenueDataSource,
  hasVenueData,
  firstGoogleTypeLabel,
  isPlaceholderCategoryLabel,
} from "common/utils";
import { entryPageFromCurrentPage } from "@/analytics/entryPage";
import { ContributionOptionsModal } from "@/components/Map/ContributionOptionsModal";
import type { ContributionAction } from "@/components/Map/PoiContributionCard";

interface VenueCardProps {
  // Core props (existing)
  onOpen?: (location: Venue) => void;
  location: Venue;
  savedPlaces: unknown;
  onSaveVenue?: (location: Venue) => void;
  index?: number;
  totalCount?: number;

  // Display control props (all default to false)
  truncateText?: boolean; // Existing
  showQRCode?: boolean; // Enable QR code modal
  showActionButtons?: boolean; // Show Get alerts/Add info
  isCompact?: boolean; // Use compact layout with fixed width

  // Conditional display props
  showAddress?: boolean; // Show MapPinIcon + address section
  showOpeningHours?: boolean; // Show OpeningHours component
  showDescription?: boolean; // Show description with"view more"
  showMetaInfo?: boolean; // Show distance|status|hours line
  showVariantIndicators?: boolean; // Show mapped/unmapped Sociability icons
  enableArrowNavigation?: boolean; // Enable arrow key focus management

  // Deprecated prop (kept for backward compatibility)
  isNearbyBusinessCard?: boolean; // Deprecated: use showQRCode instead

  // Notification system (new from HomePage)
  onNotificationClick?: (venue: Venue) => void;
  hasNotification?: boolean;

  // External auth (new from HomePage - avoids multiple useAuth calls)
  isLoggedIn?: boolean;
  onShowAuthModal?: () => void;

  inMapView?: boolean;

  /** Map snippet only: closes the popover. Renders the close button inside the grey-pin header (see inMapView + no lastUpdated). */
  onClose?: () => void;

  /** Override the pin shown in variant indicators. When omitted, falls back to orange (mapped) or grey (unmapped). */
  pinOverride?: VenuePinType;

  /** "horizontal" puts the photo beside the details instead of above them. */
  layout?: "vertical" | "horizontal";
}

function VenueCard({
  onOpen,
  location,
  savedPlaces,
  onSaveVenue,
  index,
  totalCount,
  truncateText = false,
  showQRCode = false,
  showActionButtons = false,
  isCompact = true,
  showAddress,
  showDescription,

  showOpeningHours,
  showMetaInfo,
  showVariantIndicators,

  enableArrowNavigation,
  isNearbyBusinessCard = false, // Deprecated: backward compatibility
  onNotificationClick,
  hasNotification = false,
  isLoggedIn,
  onShowAuthModal,
  inMapView = false,
  onClose,
  pinOverride,
  layout = "vertical",
}: VenueCardProps) {
  const isHorizontal = layout === "horizontal";
  // Set smart defaults based on isCompact
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const [isContributionOpen, setIsContributionOpen] = useState(false);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const i18n = useI18N();
  const { businessTypesFrontend } = useFilters();
  const { user: authUser } = useAuth();
  const { showModal } = useAuthModals();
  const isZoomedIn = useZoomDetection();
  const isDark = useDarkMode();
  const [arrowFocusIdx, setArrowFocusIdx] = useState<number | null>(null);
  const [hoveredVenueId] = useAtom(hoveredVenueIdAtom);
  const setHoveredVenueId = useSetAtom(hoveredVenueIdAtom);
  const venueNameRef = useRef<HTMLHeadingElement>(null);
  const addressRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const actionButtonsRef = useRef<HTMLDivElement>(null);
  const lastUpdatedRef = useRef<HTMLDivElement>(null);
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const qrButtonRef = useRef<HTMLButtonElement>(null);
  const getAlertsButtonRef = useRef<HTMLButtonElement>(null);
  const addInfoButtonRef = useRef<HTMLButtonElement>(null);

  // Support both internal and external auth
  const user =
    isLoggedIn !== undefined
      ? isLoggedIn
        ? { name: "UserIcon" }
        : null
      : authUser;

  // Same deep links as the map's contribution flow. /venuedetails resolves its
  // param as a slug and 404s otherwise, so never fall back to the id.
  const handleContributionAction = (action: ContributionAction) => {
    const slug = location.business?.slug || location.business?.publicUrl;
    if (!slug) {
      setIsContributionOpen(false);
      onOpen?.(location);
      return;
    }
    const search =
      action === "experiences"
        ? "tab=reviews&openModal=true"
        : action === "alerts"
          ? "notify=true"
          : "tab=photos";
    window.location.href = `/venuedetails/${slug}?${search}`;
  };

  // Handle deprecated prop
  const shouldShowQRCode = showQRCode || isNearbyBusinessCard;

  const lastUpdated = formatDateShort(location?.latestReviewDateUpdated || "");

  // Mapped/unmapped follows the same rule as the map pin. A venue can be mapped
  // with no date (e.g. an alert but no reviews), so the date is appended only
  // when we have one.
  const isMapped = hasVenueData(location);
  const dataSource = getVenueDataSource(location);
  // No team-approved review (accessibility summary) — grey pins and
  // community-contributed-only (orange, "user" source) venues both get the
  // Get Alerts/Add info CTAs; only a "Sociability Verified" venue hides them.
  const hasAccessibilitySummary = dataSource === "team";

  const BADGE = {
    team: {
      icon: "/images/sociability_verified.svg",
      label: i18n.get("sociabilityVerified"),
    },
    user: {
      icon: "/images/user_updated.svg",
      label: i18n.get("userUpdated"),
    },
  } as const;

  const PIN_SVG: Record<VenuePinType, string> = {
    [VenuePinType.Orange]: "/pins/orange_pin.svg",
    [VenuePinType.Grey]: "/pins/grey_pin.svg",
    [VenuePinType.Green]: "/pins/green_pin.svg",
    [VenuePinType.Blue]: "/pins/blue_pin.svg",
    [VenuePinType.Black]: "/pins/black_pin.svg",
    [VenuePinType.Purple]: "/pins/purple_pin.svg",
  };

  const resolvedPin = pinOverride
    ? PIN_SVG[pinOverride]
    : isMapped
      ? PIN_SVG[VenuePinType.Orange]
      : PIN_SVG[VenuePinType.Grey];

  // Build focusable elements array dynamically based on what's shown
  const focusableElements = useMemo(() => {
    const elements: Array<{
      name: string;
      ref: React.RefObject<
        HTMLDivElement | HTMLButtonElement | HTMLHeadingElement
      >;
      idx: number;
    }> = [];
    let idx = 0;

    elements.push({ name: "venueName", ref: venueNameRef, idx: idx++ }); // 0: venue name
    if (showAddress)
      elements.push({ name: "address", ref: addressRef, idx: idx++ }); // 1: address (conditional)
    if (showDescription)
      elements.push({ name: "description", ref: descriptionRef, idx: idx++ }); // 2: description (conditional)
    if (isMapped)
      elements.push({ name: "lastUpdated", ref: lastUpdatedRef, idx: idx++ }); // 3: last updated (conditional)

    // Add interactive buttons when in arrow navigation mode
    if (enableArrowNavigation) {
      if (!inMapView)
        elements.push({ name: "saveButton", ref: saveButtonRef, idx: idx++ }); // Save button
      if (shouldShowQRCode && location.business?.qrCode)
        elements.push({ name: "qrButton", ref: qrButtonRef, idx: idx++ }); // QR button
      if (showActionButtons && !hasAccessibilitySummary) {
        elements.push({
          name: "getAlertsButton",
          ref: getAlertsButtonRef,
          idx: idx++,
        }); // Get Alerts button
        elements.push({
          name: "addInfoButton",
          ref: addInfoButtonRef,
          idx: idx++,
        }); // Add InfoIcon button
      }
    }

    return elements;
  }, [
    showAddress,
    showDescription,
    showActionButtons,
    isMapped,
    hasAccessibilitySummary,
    enableArrowNavigation,
    inMapView,
    shouldShowQRCode,
    location.business?.qrCode,
  ]);

  // Helper function to get element index by name
  const getElementIndex = (name: string) => {
    return focusableElements.find((el) => el.name === name)?.idx ?? -1;
  };

  // Helper function to get tabIndex for an element
  const getTabIndex = (elementName: string) => {
    if (!enableArrowNavigation) return undefined;

    // For screen reader compatibility: make all interactive BUTTONS directly tabbable
    // This allows screen reader users to Tab through buttons normally (since screen readers
    // intercept arrow keys for their own navigation)
    const isButton = elementName.includes("Button");
    if (isButton) {
      return 0; // All buttons are always tabbable
    }

    // For non-button elements (venueName, address, description), use arrow navigation
    const elementIdx = getElementIndex(elementName);
    return elementIdx === arrowFocusIdx ? 0 : -1;
  };

  // Detect if screen reader is active (simple heuristic: if aria-live region is focused, skip roving tabindex)
  // For now, always enable roving tabindex for keyboard users

  // When VenueCard receives focus, set arrow focus to first element
  useEffect(() => {
    if (!enableArrowNavigation) return;

    const handleFocus = () => {
      setArrowFocusIdx(null); // Card is focused, not internal
    };
    const card = cardRef.current;
    if (card) {
      card.addEventListener("focus", handleFocus);
    }
    return () => {
      if (card) {
        card.removeEventListener("focus", handleFocus);
      }
    };
    // eslint-disable-next-line
  }, [enableArrowNavigation]);

  // Focus management for arrow navigation
  useEffect(() => {
    if (!enableArrowNavigation) return;

    const handleBlur = () => {
      // If focus moves outside card and its internals, reset
      setTimeout(() => {
        const active = document.activeElement;
        if (
          active !== cardRef.current &&
          active !== saveButtonRef.current &&
          active !== qrButtonRef.current &&
          active !== getAlertsButtonRef.current &&
          active !== addInfoButtonRef.current &&
          active !== venueNameRef.current &&
          active !== addressRef.current &&
          active !== lastUpdatedRef.current &&
          active !== descriptionRef.current &&
          active !== actionButtonsRef.current
        ) {
          setArrowFocusIdx(null);
        }
      }, 0);
    };
    const card = cardRef.current;
    if (card) {
      card.addEventListener("blur", handleBlur, true);
    }
    return () => {
      if (card) {
        card.removeEventListener("blur", handleBlur, true);
      }
    };
  }, [enableArrowNavigation]);

  // Handle arrow key navigation
  const handleCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Only handle keyboard events when the card itself is the target, not child buttons
    // This prevents interference with save button, QR button, etc.
    const isTargetCard = e.currentTarget === e.target;

    // Prevent Tab from triggering any actions - let it work naturally for navigation
    if (e.key === "Tab") {
      // Let Tab work naturally, but don't trigger any card actions
      return;
    }

    // Handle Enter and Space keys for accessibility (WCAG Level A)
    if (e.key === "Enter" || e.key === " ") {
      // Only open venue if the card itself was activated, not a child button
      if (isTargetCard) {
        e.preventDefault();
        onOpen?.(location);
        return;
      }
      // Let child buttons handle their own events
      return;
    }

    // Arrow navigation only when enabled
    if (!enableArrowNavigation) return;

    // Prevent carousel scrolling when navigating inside card
    if (
      e.key === "ArrowRight" ||
      e.key === "ArrowLeft" ||
      e.key === "ArrowDown" ||
      e.key === "ArrowUp"
    ) {
      e.stopPropagation();
    }

    if (arrowFocusIdx === null) {
      // Card is focused, start arrow navigation on right/left
      if (e.key === "ArrowRight") {
        e.preventDefault();
        setArrowFocusIdx(0);
        venueNameRef.current?.focus();
      }
    }
  };

  const handleInternalKeyDown = (
    e: React.KeyboardEvent<
      HTMLDivElement | HTMLButtonElement | HTMLHeadingElement
    >,
    idx: number,
  ) => {
    if (!enableArrowNavigation) return;

    const elements = focusableElements;
    const maxIdx = elements.length - 1;

    if (e.key === "ArrowRight") {
      e.preventDefault();
      const nextIdx = idx + 1;

      if (nextIdx > maxIdx) {
        // Wrap back to card
        cardRef.current?.focus();
        setArrowFocusIdx(null);
      } else {
        // Focus next element
        const nextElement = elements.find((el) => el.idx === nextIdx);
        if (nextElement?.ref.current) {
          nextElement.ref.current.focus();
          setArrowFocusIdx(nextIdx);
        } else {
          // If ref is null, skip to next element
          if (nextIdx + 1 <= maxIdx) {
            // Try next element
            const skipElement = elements.find((el) => el.idx === nextIdx + 1);
            if (skipElement?.ref.current) {
              skipElement.ref.current.focus();
              setArrowFocusIdx(nextIdx + 1);
            }
          }
        }
      }
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      const prevIdx = idx - 1;

      if (prevIdx < 0) {
        // Wrap back to card
        cardRef.current?.focus();
        setArrowFocusIdx(null);
      } else {
        // Focus previous element
        const prevElement = elements.find((el) => el.idx === prevIdx);
        prevElement?.ref.current?.focus();
        setArrowFocusIdx(prevIdx);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      cardRef.current?.focus();
      setArrowFocusIdx(null);
    }
  };

  const cardRef = useRef<HTMLDivElement>(null);

  const handleOpenAuthModal = () => {
    if (!user) {
      if (onShowAuthModal) {
        onShowAuthModal();
      } else {
        showModal(AuthModalScreen.EnterEmail);
      }
    }
  };

  const businessTypeImage =
    bucketUrlPrefix +
    findImageByName(
      location?.calcVenueType || location?.businessType,
      businessTypesFrontend,
    );

  let businessPhoto = "";
  if (location?.photo) {
    businessPhoto = bucketUrlPrefix + location?.photo;
  }

  let businessphotoThumb = "";
  if (location?.photoThumb) {
    businessphotoThumb = bucketUrlPrefix + location?.photoThumb;
  } else {
    businessphotoThumb = businessTypeImage;
  }

  // Function to handle image load failure
  const handleImageLoadError = () => {
    setImageLoadFailed(true);
  };

  // CheckIcon if image source exists
  const getImageSrc = () => {
    if (isCompact) {
      return location.photoThumb
        ? bucketUrlPrefix + location.photoThumb
        : location.photo
          ? bucketUrlPrefix + location.photo
          : "";
      // : businessTypeImage; // Fallback to business type image
    } else {
      return isZoomedIn
        ? businessPhoto !== ""
          ? businessPhoto
          : businessphotoThumb
        : businessphotoThumb;
    }
  };

  const imageSrc = getImageSrc();

  const shouldShowPlaceholder = !imageSrc || imageLoadFailed;
  // Only show placeholder if image load fails, not when businessTypeImage is used
  // const shouldShowPlaceholder = imageLoadFailed;

  const isVenueSaved = (location) => {
    if (savedPlaces && location?.business?.id) {
      const flattened = (savedPlaces as any)
        ?.map((e) => e.saved_place_details.map((e) => e.business.id))
        .flat();
      const result = flattened.some((id) => id === location.business.id);
      return result;
    }
    return false;
  };

  const isHighlighted = hoveredVenueId === location.id;

  // Map snippet for a grey-pin / not-yet-in-DB venue: plain grey header (badge
  // + save + close), no photo, per the redesign. Mapped venues and every
  // other VenueCard usage keep the existing photo header untouched.
  const isMapSnippetUnmapped = inMapView && !isMapped;

  // Get category/business type. Venues we haven't classified into a
  // Sociability category (e.g. museums, galleries) fall back to Google's own
  // place type rather than the generic "Venue" label — the "this category
  // isn't supported yet" banner elsewhere already covers that messaging.
  // businessType can itself be the backend's literal "Venue" placeholder
  // (not a real classification), so that's treated the same as empty.
  const category =
    location.calcVenueType ||
    (!isPlaceholderCategoryLabel(location.businessType)
      ? location.businessType
      : "") ||
    firstGoogleTypeLabel(location.types) ||
    "Venue";

  // For compact mode, we need different data handling
  const distance = location.distance || location.userDistance || "";

  // Whether the meta-info line (distance | status | hours) has anything to
  // show. Distance isn't always present — rendering the line unconditionally
  // on `showMetaInfo` left an empty line taking up space on cards without it,
  // so cards with and without distance had different vertical rhythm.
  const hasOpeningHoursMeta =
    location.businessStatus !== "CLOSED_TEMPORARILY" &&
    !!showOpeningHours &&
    !!location.openingHours &&
    location.openingHours[0]?.mode === 0;
  const hasMetaInfo =
    !!showMetaInfo &&
    !!(
      distance ||
      location.businessStatus === "CLOSED_TEMPORARILY" ||
      hasOpeningHoursMeta
    );

  // Get description (for compact mode)
  const getDescription = () => {
    if (location.details?.description) {
      return location.details.description;
    }
    if (location.business?.venue_summary) {
      return location.business.venue_summary;
    }
    return "";
  };

  const description = getDescription();

  // Accessible modal close: unmount and return focus to trigger (WCAG 2.2.1, 2.1.2)
  const handleCloseQRModal = () => {
    setIsQRModalOpen(false);
    // Return focus to the button that opened the modal after it unmounts
    setTimeout(() => {
      qrButtonRef.current?.focus();
    }, 0);
  };

  // Unified card layout
  return (
    <article className="h-full flex flex-col min-h-0">
      <Card
        id={`venue-card-${location.id}`}
        className={cn(
          "rounded-[8px] p-0 focus-blue transition-all duration-200 group",
          "flex flex-1 min-h-0 min-w-[200px] overflow-hidden flex-shrink-0",
          // Spans both grid columns, so match the height of a two-column card.
          isHorizontal ? "flex-row items-stretch min-h-[200px]" : "flex-col",
          isHighlighted && !inMapView ? "ring-1 ring-focusRing-default" : "",
          "border border-border-token-brand-minimal",
          "bg-bg-brand-secondary-default",
          "hover:bg-bg-brand-secondary-hover",
          "active:bg-bg-brand-secondary-active",
          inMapView ? "" : "m-1",
        )}
        tabIndex={enableArrowNavigation ? 0 : -1}
        aria-label={`${location.name}${index !== undefined && totalCount ? `, ${index + 1} of ${totalCount}` : ""}, ${location.businessType}`}
        onClick={() => onOpen(location)}
        ref={cardRef}
        onKeyDown={handleCardKeyDown}
        onMouseEnter={() => { if (!inMapView) setHoveredVenueId(location.id); }}
        onMouseLeave={() => { if (!inMapView) setHoveredVenueId(""); }}
      >
        {/* Grey-pin map snippet: plain grey header (badge + save + close), no photo. */}
        {isMapSnippetUnmapped ? (
          <div className="rounded-t-[8px] bg-[#F2F3F4] px-3 py-3 flex items-center justify-between gap-2">
            <div
              className="inline-flex items-center gap-1 min-w-0 rounded-3xl border border-border-token-neutral-minimal bg-bg-brand-secondary-default px-3 py-1.5"
              tabIndex={-1}
              aria-label={`Category: ${location.calcVenueType || location.businessType}`}
            >
              <BusinessTypeIcon
                businessType={
                  location.calcVenueType || location.businessType || "venue"
                }
                className="w-4 h-4 text-fg-neutral-strong shrink-0"
              />
              <span className="label-s-medium text-fg-neutral-strong truncate">
                {category}
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                ref={saveButtonRef}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  !user?.name ? handleOpenAuthModal() : onSaveVenue(location);
                }}
                className="flex items-center justify-center w-8 h-8 bg-bg-brand-secondary-default border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover transition-colors focus-blue"
                aria-label={`${isVenueSaved(location) ? "Remove" : "Add"} ${location.name} ${isVenueSaved(location) ? "from" : "to"} saved places`}
                aria-pressed={isVenueSaved(location)}
              >
                <HeartStraightIcon
                  size={16}
                  weight={isVenueSaved(location) ? "fill" : "regular"}
                  className="text-fg-neutral-strong"
                  aria-hidden="true"
                />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose?.();
                }}
                className="flex items-center justify-center w-8 h-8 bg-bg-brand-secondary-default border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover transition-colors focus-blue"
                aria-label="Close venue preview"
              >
                <XPhosphorIcon
                  size={16}
                  weight="bold"
                  className="text-fg-neutral-strong"
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        ) : (
        <CardHeader
          className={cn(
            "p-0",
            isHorizontal && "w-[calc(50%-12px)] flex-shrink-0",
          )}
        >
          <figure
            className={cn(
              "relative overflow-hidden bg-container-brand-minimal",
              isHorizontal
                ? "rounded-l-[8px] w-full h-full"
                : "rounded-t-[8px] w-full aspect-[20/9] flex-shrink-0",
            )}
            role="none"
          >
            {/* Business image or placeholder */}
            {shouldShowPlaceholder ? (
              <div className="w-full h-full relative" aria-hidden="true">
                {/* Background layer - Checkerboard pattern */}
                <div
                  className="absolute inset-0 w-full h-full"
                  style={{
                    backgroundImage:
                      "url(/images/No_Data_Placeholder_Images_Background.png)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                  }}
                />
                {/* Foreground layer - Sociability icon centered */}
                <img
                  src="/images/No_Data_Placeholder_Images_Foreground.png"
                  alt=""
                  aria-hidden="true"
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200px] h-[200px] object-contain z-10"
                />
              </div>
            ) : (
              <img
                src={imageSrc}
                alt={location.altText || `Photo of ${location.name}`}
                loading="lazy"
                decoding="async"
                className={cn(
                  "w-full h-full object-cover cursor-pointer",
                  "absolute inset-0 bg-container-neutral-minimal",
                )}
                onError={handleImageLoadError}
                aria-hidden="true"
              />
            )}

            {/* Category Badge */}
            <div
              className="absolute top-3 left-3 opacity-90 px-3 py-1 bg-bg-brand-tertiary-default rounded-3xl flex items-center"
              tabIndex={-1}
              aria-label={`Category: ${location.calcVenueType || location.businessType}`}
            >
              <div className="mr-1" aria-hidden="true">
                <BusinessTypeIcon
                  businessType={
                    location.calcVenueType || location.businessType || "venue"
                  }
                  className="w-4 h-4 text-fg-neutral-strong"
                />
              </div>
              <span className="label-s-medium text-fg-neutral-strong">
                {location.nearbyBusinessData?.[0]
                  ?.business_nearby_category_title
                  ? `${location.nearbyBusinessData[0].business_nearby_category_title}`
                  : category}
              </span>
            </div>

            {/* Add to saved places */}
            {!inMapView && (
              <button
                ref={saveButtonRef}
                type="button"
                tabIndex={
                  enableArrowNavigation ? getTabIndex("saveButton") : undefined
                }
                onClick={(e) => {
                  e.stopPropagation();
                  !user?.name ? handleOpenAuthModal() : onSaveVenue(location);
                }}
                onKeyDown={(e) => {
                  // Handle arrow navigation
                  if (enableArrowNavigation) {
                    const saveIdx = getElementIndex("saveButton");
                    if (saveIdx !== -1) {
                      handleInternalKeyDown(e, saveIdx);
                    }
                  }
                  // Prevent event from bubbling to card's keydown handler
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                  }
                }}
                className="absolute right-2 top-2 flex items-center justify-center w-7 h-7 md:w-8 md:h-8 bg-surface-primary border border-border-token-neutral-minimal rounded-full hover:bg-bg-brand-ghost-hover transition-colors focus-blue"
                aria-label={`${isVenueSaved(location) ? "Remove" : "Add"} ${location.name} ${isVenueSaved(location) ? "from" : "to"} saved places`}
                aria-pressed={isVenueSaved(location)}
              >
                <HeartStraightIcon
                  size={16}
                  weight={isVenueSaved(location) ? "fill" : "regular"}
                  className="text-fg-neutral-strong"
                  aria-hidden="true"
                />
              </button>
            )}

            {shouldShowQRCode && location.business?.qrCode && (
              <button
                ref={qrButtonRef}
                type="button"
                tabIndex={
                  enableArrowNavigation ? getTabIndex("qrButton") : undefined
                }
                className="absolute bottom-2 right-2 p-1 bg-opacity-30 border-0 cursor-pointer"
                aria-label="View QR code"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsQRModalOpen(true);
                }}
                onKeyDown={(e) => {
                  // Handle arrow navigation
                  if (enableArrowNavigation) {
                    const qrIdx = getElementIndex("qrButton");
                    if (qrIdx !== -1) {
                      handleInternalKeyDown(e, qrIdx);
                    }
                  }
                  // Prevent event from bubbling to card's keydown handler
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                  }
                }}
              >
                <img
                  src={bucketUrlPrefix + location.business.qrCode}
                  alt={`QR code for ${location.name}`}
                  className="w-12 h-12 object-contain mt-2"
                />
              </button>
            )}
          </figure>
        </CardHeader>
        )}

        <CardContent
          className={cn(
            "px-3 py-2 flex flex-1 flex-col min-h-0 gap-1",
            isHorizontal && "justify-center gap-2 px-4 py-4",
            isMapSnippetUnmapped && "px-4 pt-3 pb-4 gap-4",
          )}
        >
          <div
            className={cn(
              "flex flex-col gap-1",
              isHorizontal && "gap-2",
              isMapSnippetUnmapped && "gap-0.5",
            )}
          >
            {/* Venue Name - always shown with compact typography */}
            <h3
              id={`venue-${location.id}`}
              className={cn(
                isMapSnippetUnmapped ? "label-l-bold" : "body-l-semibold",
                "text-fg-neutral-strong line-clamp-1",
                truncateText && "truncate text-ellipsis",
                enableArrowNavigation &&
                  "cursor-pointer focus-blue rounded outline-none",
              )}
              {...(enableArrowNavigation
                ? {
                    ref: venueNameRef,
                    tabIndex: getTabIndex("venueName"),
                    "aria-label": `${location.name}, click to view details`,
                    onClick: (e: React.MouseEvent) => {
                      e.stopPropagation();
                      onOpen(location);
                    },
                    onKeyDown: (e: React.KeyboardEvent<HTMLHeadingElement>) => {
                      const venueNameIdx = getElementIndex("venueName");
                      if (venueNameIdx !== -1) {
                        handleInternalKeyDown(e, venueNameIdx);
                      }
                      // Handle Enter/Space to open venue
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onOpen?.(location);
                      }
                    },
                  }
                : {})}
            >
              {location.name}
            </h3>

            {/* "User Verified" is now covered by the Community Contributed badge below;
                "Sociability Verified" / "Owner Verified" are distinct, staff-set states. */}
            {location.businessVerified != null &&
              location.businessVerified !== "User Verified" && (
              <div className="flex items-center gap-1">
                <img
                  src="/images/SealCheck.svg"
                  alt=""
                  aria-hidden="true"
                  className="w-4 h-4 shrink-0"
                />
                <span className="label-s-medium text-fg-neutral-subtle whitespace-nowrap">
                  {location.businessVerified}
                </span>
                {location.businessVerifiedDatetime && (
                  <span className="label-s-medium text-fg-neutral-subtle whitespace-nowrap">
                    {`: ${new Date(location.businessVerifiedDatetime.replace(" ", "T")).toLocaleDateString("en-GB", { month: "long", year: "numeric" })}`}
                  </span>
                )}
              </div>
            )}

            {/* Meta InfoIcon - conditional */}
            {hasMetaInfo && (
              <p
                className={cn(
                  "text-fg-neutral-subtle line-clamp-1",
                  isMapSnippetUnmapped ? "label-m-medium" : "label-s-regular",
                )}
              >
                {distance ? `${distance} mile` : null}
                {/* Temporarily Closed Status */}
                {location.businessStatus === "CLOSED_TEMPORARILY" && (
                  <>
                    <span className="text-fg-neutral-minimal"> | </span>
                    <span className="label-s-regular text-fg-error-subtle">
                      {i18n.get("temporarilyClosed")}
                    </span>
                  </>
                )}
                {/* Opening Hours (arrow focusable, if present) - only show if NOT temporarily closed */}
                {location.businessStatus !== "CLOSED_TEMPORARILY" &&
                  showOpeningHours &&
                  location.openingHours &&
                  location.openingHours[0]?.mode === 0 && (
                    <>
                      {distance ? (
                        <span className="text-fg-neutral-minimal"> | </span>
                      ) : null}
                      <OpeningHours
                        openingHours={location.openingHours}
                        containerClassName=""
                        className={cn(
                          "text-fg-neutral-subtle",
                          isMapSnippetUnmapped
                            ? "label-m-medium"
                            : "label-s-regular",
                        )}
                      />
                    </>
                  )}
              </p>
            )}

            {/* Address section - conditional */}
            {showAddress && (
              <div
                role="button"
                aria-label={`Address, open ${location.name} details`}
                ref={addressRef}
                tabIndex={getTabIndex("address")}
                onKeyDown={(e) => {
                  if (enableArrowNavigation) {
                    const addressIdx = getElementIndex("address");
                    if (addressIdx !== -1) {
                      handleInternalKeyDown(e, addressIdx);
                    }
                  }
                  // Handle Enter/Space to open venue
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onOpen?.(location);
                  }
                }}
                className="flex items-start gap-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 p-1 -m-1"
              >
                <MapPinIcon
                  size={16}
                  className="flex-shrink-0 mt-0.5 text-fg-neutral-subtle"
                  aria-hidden="true"
                />
                <span className="label-s-medium text-fg-neutral-subtle line-clamp-1">
                  {location.address}
                </span>
              </div>
            )}

            {/* Variant indicators - conditional */}
            {showVariantIndicators && isMapped && dataSource !== "none" && (
              <div
                role="button"
                aria-label={`${BADGE[dataSource].label}, open ${location.name} details`}
                ref={lastUpdatedRef}
                tabIndex={getTabIndex("lastUpdated")}
                onKeyDown={(e) => {
                  if (enableArrowNavigation) {
                    const lastUpdatedIdx = getElementIndex("lastUpdated");
                    if (lastUpdatedIdx !== -1) {
                      handleInternalKeyDown(e, lastUpdatedIdx);
                    }
                  }
                  // Handle Enter/Space to open venue
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onOpen?.(location);
                  }
                }}
                className="flex items-center gap-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 p-1 -m-1"
              >
                <img
                  src={BADGE[dataSource].icon}
                  alt=""
                  aria-hidden="true"
                  className="w-4 h-4"
                />
                <span className="label-s-medium text-fg-neutral-subtle">
                  {BADGE[dataSource].label}
                  {lastUpdated ? `: ${lastUpdated}` : ""}
                </span>
              </div>
            )}

            {showVariantIndicators && !isMapped && (
              <div
                className={cn(
                  "flex items-center gap-2",
                  isMapSnippetUnmapped && "mt-1.5",
                )}
              >
                {inMapView ? (
                  <AccessibilityInfoIcon className="w-4 h-4 shrink-0 text-fg-brand-subtle" />
                ) : (
                  <img
                    src={resolvedPin}
                    alt=""
                    aria-hidden="true"
                    className="w-4 h-4"
                  />
                )}
                <span
                  className={cn(
                    inMapView
                      ? "label-m-medium text-fg-neutral-strong"
                      : "label-s-medium text-fg-neutral-subtle",
                  )}
                >
                  {i18n.get("beTheFirstToAddAccessibilityInfo")}
                </span>
              </div>
            )}
          </div>

          {/* Gated on hasAccessibilitySummary, not isMapped — community-contributed
              venues have no team summary, so this was leaving a blank gap above the CTAs. */}
          {showDescription && hasAccessibilitySummary && (
            <div
              role="button"
              aria-label={`Description, open ${location.name} details`}
              ref={descriptionRef}
              tabIndex={description ? getTabIndex("description") : undefined}
              onKeyDown={(e) => {
                if (enableArrowNavigation && description) {
                  const descIdx = getElementIndex("description");
                  if (descIdx !== -1) {
                    handleInternalKeyDown(e, descIdx);
                  }
                }
                // Handle Enter/Space to open venue
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onOpen?.(location);
                }
              }}
              className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 p-1 -m-1 min-h-[2.5rem]"
            >
              <p className="body-m-medium text-fg-neutral-strong line-clamp-2 min-h-[2rem]">
                {description ? (
                  <>
                    {description} ...
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpen?.(location);
                      }}
                      onKeyDown={(e) => {
                        // Prevent event from bubbling to card's keydown handler
                        if (e.key === "Enter" || e.key === " ") {
                          e.stopPropagation();
                        }
                      }}
                      className="underline text-fg-brand-subtle hover:text-fg-brand-strong p-0 bg-transparent border-0 transition-colors cursor-pointer focus-blue"
                      aria-label={`View more details for ${location.name}`}
                    >
                      {i18n.get("viewMore")}
                    </button>
                  </>
                ) : (
                  <span className="invisible">&nbsp;</span>
                )}
              </p>
            </div>
          )}

          {/* Action Buttons: unmapped (grey) and community-contributed-only
              (no team accessibility summary) venues both get these. */}
          {showActionButtons && !hasAccessibilitySummary && (
            <div
              ref={actionButtonsRef}
              className="self-stretch flex flex-wrap justify-center items-stretch gap-3 w-full"
            >
              <button
                ref={getAlertsButtonRef}
                type="button"
                tabIndex={
                  enableArrowNavigation
                    ? getTabIndex("getAlertsButton")
                    : undefined
                }
                className={cn(
                  "flex-1 basis-28 min-w-28 min-h-10 px-3 py-2 rounded-lg",
                  "flex justify-center items-center gap-2 transition-colors cursor-pointer focus-blue",
                  hasNotification
                    ? "border border-border-token-brand-minimal bg-bg-brand-secondary-active hover:bg-bg-brand-secondary-hover active:bg-bg-brand-secondary-active"
                    : "border border-border-token-brand-minimal bg-bg-brand-secondary-default hover:bg-bg-brand-secondary-hover active:bg-bg-brand-secondary-active",
                )}
                onClick={(e) => {
                  e.stopPropagation();

                  // Map snippet has no onNotificationClick wired up (it's a
                  // list-page concept) — fall back to the same deep link
                  // "Add info" uses, just aimed at the alerts tab.
                  if (onNotificationClick) {
                    onNotificationClick(location);
                  } else {
                    handleContributionAction("alerts");
                  }
                }}
                onKeyDown={(e) => {
                  // Handle arrow navigation
                  if (enableArrowNavigation) {
                    const alertsIdx = getElementIndex("getAlertsButton");
                    if (alertsIdx !== -1) {
                      handleInternalKeyDown(e, alertsIdx);
                    }
                  }
                  // Prevent event from bubbling to card's keydown handler
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                  }
                }}
                aria-label={
                  hasNotification
                    ? `Notifications enabled for ${location.name}`
                    : `Get notifications for ${location.name}`
                }
              >
                {hasNotification ? (
                  <BellRingingIcon
                    size={16}
                    className="shrink-0 text-fg-neutral-strong"
                    weight="fill"
                    aria-hidden="true"
                  />
                ) : (
                  <BellIcon
                    size={16}
                    className="shrink-0 text-fg-neutral-strong"
                    weight="regular"
                    aria-hidden="true"
                  />
                )}
                <div
                  className={cn(
                    "text-center justify-center leading-4 whitespace-nowrap text-fg-neutral-strong",
                    "label-s-medium",
                  )}
                  aria-hidden="true"
                >
                  {i18n.get("getAlerts")}
                </div>
              </button>
              <button
                ref={addInfoButtonRef}
                type="button"
                tabIndex={
                  enableArrowNavigation
                    ? getTabIndex("addInfoButton")
                    : undefined
                }
                className={cn(
                  "flex-1 basis-28 min-w-28 min-h-10 px-3 py-2 rounded-[8px]",
                  "flex justify-center items-center gap-2",
                  "transition-colors cursor-pointer focus-blue",
                  // Primary CTA — filled on the horizontal focus card and the map snippet.
                  isHorizontal || inMapView
                    ? "border border-transparent bg-bg-brand-primary-default text-white hover:bg-bg-brand-primary-hover"
                    : "border border-border-token-brand-minimal bg-bg-brand-secondary-default hover:bg-bg-brand-secondary-hover active:bg-bg-brand-secondary-active",
                )}
                onClick={(e) => {
                  e.stopPropagation();

                  // place_info_add_cta_activated — the "Add info" CTA on a venue card. trackEvent
                  // primitive (the card is shared across home / search / saved — no single page
                  // scope); entry_page captures which page it was tapped on.
                  trackEvent("place_info_add_cta_activated", {
                    brand_id: location.brandId != null ? String(location.brandId) : "",
                    brand_name: location.brandName ?? "",
                    business_id: location.business?.id?.toString() ?? "",
                    entry_page: entryPageFromCurrentPage(),
                    pin_type: pinTypeForVenue(location),
                    place_name: location?.name ?? "",
                  });

                  setIsContributionOpen(true);
                }}
                onKeyDown={(e) => {
                  // Handle arrow navigation
                  if (enableArrowNavigation) {
                    const addInfoIdx = getElementIndex("addInfoButton");
                    if (addInfoIdx !== -1) {
                      handleInternalKeyDown(e, addInfoIdx);
                    }
                  }
                  // Prevent event from bubbling to card's keydown handler
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                  }
                }}
                aria-label={`Add information for ${location.name}`}
              >
                <PlusCircleIcon
                  size={16}
                  className="shrink-0"
                  weight={isHorizontal || inMapView ? "fill" : "regular"}
                  aria-hidden="true"
                />
                <div
                  className={cn(
                    "text-center justify-center leading-4 whitespace-nowrap label-s-medium",
                    isHorizontal || inMapView ? "text-white" : "text-fg-neutral-strong",
                  )}
                  aria-hidden="true"
                >
                  {i18n.get("addInfo")}
                </div>
              </button>
            </div>
          )}
        </CardContent>

        {isContributionOpen && (
          <ContributionOptionsModal
            onSelectAction={handleContributionAction}
            onClose={() => setIsContributionOpen(false)}
          />
        )}

        {isQRModalOpen && (
          <Modal
            focusTrapped
            center
            blockScroll={true}
            animationDuration={0}
            open={isQRModalOpen}
            onClose={handleCloseQRModal}
            classNames={{
              modal:
                "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
              closeButton: "hidden",
            }}
          >
            <section
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-title"
              className="flex flex-col bg-bg-brand-secondary-default rounded-xl overflow-y-auto"
            >
              <header className="py-4 min-h-14 flex items-center justify-between text-center px-6 border-b border-border-token-brand-minimal sticky top-0">
                <StandardButton
                  onClick={handleCloseQRModal}
                  variant="closeModal"
                  icon={<XIcon color={isDark ? "#A39FE8" : "#352FC3"} />}
                  aria-label="Close modal"
                  tabIndex={0}
                  className="flex-shrink-0"
                />
                <h1
                  className="title-s-semibold text-center text-fg-neutral-strong flex-1 px-4"
                  id="modal-title"
                >
                  {location.name}
                </h1>
                <div className="flex-shrink-0 w-10"></div>
              </header>
              <div className="flex-grow overflow-y-auto py-3 px-3">
                <div className="w-full flex justify-center">
                  {location.business?.qrCode && (
                    <img
                      src={bucketUrlPrefix + location.business.qrCode}
                      alt={`QR code for ${location.name}`}
                      className="w-82 h-82 object-contain"
                    />
                  )}
                </div>
              </div>
            </section>
          </Modal>
        )}
      </Card>
    </article>
  );
}

export default memo(VenueCard);
